import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { adminApi } from "../../services/adminApi";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { StatTile } from "../../components/common/StatTile";
import { GapNotice } from "../../components/common/GapNotice";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";

export function CostMonitoringPage() {
  const [costs, setCosts] = useState(null);
  const [aiCosts, setAiCosts] = useState(null);

  useEffect(() => {
    adminApi.costs().then((r) => setCosts(r.costs)).catch(() => setCosts([]));
    adminApi.aiCosts().then(setAiCosts).catch(() => setAiCosts({ by_feature: [], totals: { total_calls: 0, total_cost_usd: 0, any_stubbed: true } }));
  }, []);

  const byService = useMemo(() => {
    if (!costs) return [];
    const grouped = {};
    costs.forEach((c) => { grouped[c.service] = (grouped[c.service] || 0) + Number(c.cost); });
    return Object.entries(grouped).map(([service, cost]) => ({ service, cost }));
  }, [costs]);

  const byDate = useMemo(() => {
    if (!costs) return [];
    const grouped = {};
    costs.forEach((c) => {
      grouped[c.date] ||= { date: c.date, cost: 0, users: 0 };
      grouped[c.date].cost += Number(c.cost);
      grouped[c.date].users = Math.max(grouped[c.date].users, c.user_count);
    });
    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  }, [costs]);

  if (!costs) return <Spinner />;

  const totalCost = byService.reduce((sum, s) => sum + s.cost, 0);
  const latestUserCount = costs.length ? Math.max(...costs.map((c) => c.user_count)) : 0;
  const costPerUser = latestUserCount > 0 ? totalCost / latestUserCount : 0;

  return (
    <div>
      <h1>Cost Monitoring</h1>

      <div className="section-spacing" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <StatTile label="Total Cost (tracked period)" value={`$${totalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}`} />
        <StatTile label="Cost per Active User" value={`$${costPerUser.toFixed(4)}`} />
        <StatTile label="Services Tracked" value={byService.length} />
      </div>

      <Card title="Azure Cost Breakdown by Service" className="section-spacing">
        {byService.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)" }}>No cost data recorded yet in audit.CostTracking.</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byService}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="service" />
              <YAxis />
              <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
              <Bar dataKey="cost" fill="#f2a900" name="Cost" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Cost Trends" className="section-spacing">
        {byDate.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)" }}>No trend data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={byDate}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#f2a900" name="Total Cost ($)" />
              <Line yAxisId="right" type="monotone" dataKey="users" stroke="#3b82c4" name="Active Users" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card
        title="AI Usage & Cost"
        className="section-spacing"
        actions={aiCosts?.totals?.any_stubbed ? <Badge tone="warning">Stubbed -- no real API costs yet</Badge> : null}
      >
        {!aiCosts ? (
          <Spinner />
        ) : (
          <>
            {aiCosts.totals?.any_stubbed && (
              <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                The AI integration is currently a stub (mock responses, no real Anthropic API calls yet), so
                total_cost_usd below is always $0.00 -- this reflects an unfinished integration, not a real cost
                saving.
              </p>
            )}
            <div className="section-spacing" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
              <StatTile label="Total AI Calls" value={aiCosts.totals?.total_calls ?? 0} />
              <StatTile
                label="Total AI Cost"
                value={`$${(aiCosts.totals?.total_cost_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                sublabel={aiCosts.totals?.any_stubbed ? "Stubbed -- not a real figure yet" : undefined}
              />
              <StatTile label="Features Using AI" value={aiCosts.by_feature?.length ?? 0} />
            </div>
            <DataTable
              rows={aiCosts.by_feature}
              rowKey="feature_name"
              emptyTitle="No AI usage recorded yet"
              columns={[
                { key: "feature_name", header: "Feature" },
                { key: "call_count", header: "Calls" },
                { key: "cache_hits", header: "Cache Hits" },
                { key: "total_cost_usd", header: "Cost", render: (r) => `$${Number(r.total_cost_usd).toFixed(2)}` },
                { key: "avg_latency_ms", header: "Avg Latency (ms)" },
                { key: "last_call", header: "Last Call", render: (r) => (r.last_call ? new Date(r.last_call).toLocaleString() : "Never") },
              ]}
            />
          </>
        )}
      </Card>

      <Card title="Budget Alerts, Forecasting, Optimization & Dev vs. Prod">
        <GapNotice>
          audit.CostTracking (V016) has no `environment` column, so today's data can't be split into Dev vs. Prod at
          all -- that needs either a new column tagging each row, or two separate cost-export pipelines feeding
          distinct rows per environment. Budget alert thresholds, next-month cost forecasting, and optimization
          suggestions all need new logic beyond what's stored here (a threshold-config table for alerts, a simple
          trend-projection model for forecasting, and either a rules engine or a call out to Azure Advisor's
          recommendations API for optimization suggestions) -- none of that exists yet.
        </GapNotice>
      </Card>
    </div>
  );
}
